import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Building2,
  MapPin,
  Calendar,
  Lock,
  Sparkles,
  Check,
  ArrowRight,
  UserRound,
  Mail,
} from "lucide-react";
import { auth } from "../../services/auth";
import { usePublicBrand } from "../../context/BrandContext";
import LeftPanel from "./LeftPanel";
import {
  FField,
  SelField,
  StrBar,
  ErrBox,
  BackBtn,
  BtnPrimary,
  AuthLink,
  Spinner,
  pwStrength,
  generatePassword,
  ROLES,
} from "./AuthComponents";
import "./auth-phone.css";

/* ══════════════════════════════════════════════════════════════════
   STATIC DATA
══════════════════════════════════════════════════════════════════ */

const US_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

const CITIES_BY_STATE = {
  Alabama: [
    "Birmingham",
    "Montgomery",
    "Huntsville",
    "Mobile",
    "Tuscaloosa",
    "Hoover",
    "Dothan",
    "Auburn",
    "Decatur",
    "Madison",
  ],
  Alaska: [
    "Anchorage",
    "Fairbanks",
    "Juneau",
    "Sitka",
    "Ketchikan",
    "Wasilla",
    "Kenai",
    "Kodiak",
    "Bethel",
    "Palmer",
  ],
  Arizona: [
    "Phoenix",
    "Tucson",
    "Mesa",
    "Chandler",
    "Scottsdale",
    "Glendale",
    "Gilbert",
    "Tempe",
    "Peoria",
    "Surprise",
  ],
  Arkansas: [
    "Little Rock",
    "Fort Smith",
    "Fayetteville",
    "Springdale",
    "Jonesboro",
    "North Little Rock",
    "Conway",
    "Rogers",
    "Bentonville",
    "Pine Bluff",
  ],
  California: [
    "Los Angeles",
    "San Diego",
    "San Jose",
    "San Francisco",
    "Fresno",
    "Sacramento",
    "Long Beach",
    "Oakland",
    "Bakersfield",
    "Anaheim",
  ],
  Colorado: [
    "Denver",
    "Colorado Springs",
    "Aurora",
    "Fort Collins",
    "Lakewood",
    "Thornton",
    "Arvada",
    "Westminster",
    "Pueblo",
    "Centennial",
  ],
  Connecticut: [
    "Bridgeport",
    "New Haven",
    "Hartford",
    "Stamford",
    "Waterbury",
    "Norwalk",
    "Danbury",
    "New Britain",
    "West Hartford",
    "Greenwich",
  ],
  Delaware: [
    "Wilmington",
    "Dover",
    "Newark",
    "Middletown",
    "Smyrna",
    "Milford",
    "Seaford",
    "Georgetown",
    "Elsmere",
    "New Castle",
  ],
  Florida: [
    "Jacksonville",
    "Miami",
    "Tampa",
    "Orlando",
    "St. Petersburg",
    "Hialeah",
    "Tallahassee",
    "Fort Lauderdale",
    "Port St. Lucie",
    "Cape Coral",
  ],
  Georgia: [
    "Atlanta",
    "Columbus",
    "Augusta",
    "Macon",
    "Savannah",
    "Athens",
    "Sandy Springs",
    "Roswell",
    "Johns Creek",
    "Albany",
  ],
  Hawaii: [
    "Honolulu",
    "Pearl City",
    "Hilo",
    "Kailua",
    "Waipahu",
    "Kaneohe",
    "Mililani",
    "Kahului",
    "Ewa Beach",
    "Kihei",
  ],
  Idaho: [
    "Boise",
    "Meridian",
    "Nampa",
    "Idaho Falls",
    "Pocatello",
    "Caldwell",
    "Coeur d'Alene",
    "Twin Falls",
    "Lewiston",
    "Post Falls",
  ],
  Illinois: [
    "Chicago",
    "Aurora",
    "Rockford",
    "Joliet",
    "Naperville",
    "Springfield",
    "Peoria",
    "Elgin",
    "Waukegan",
    "Champaign",
  ],
  Indiana: [
    "Indianapolis",
    "Fort Wayne",
    "Evansville",
    "South Bend",
    "Carmel",
    "Fishers",
    "Bloomington",
    "Hammond",
    "Gary",
    "Muncie",
  ],
  Iowa: [
    "Des Moines",
    "Cedar Rapids",
    "Davenport",
    "Sioux City",
    "Iowa City",
    "Waterloo",
    "Ames",
    "West Des Moines",
    "Council Bluffs",
    "Dubuque",
  ],
  Kansas: [
    "Wichita",
    "Overland Park",
    "Kansas City",
    "Olathe",
    "Topeka",
    "Lawrence",
    "Shawnee",
    "Manhattan",
    "Lenexa",
    "Salina",
  ],
  Kentucky: [
    "Louisville",
    "Lexington",
    "Bowling Green",
    "Owensboro",
    "Covington",
    "Hopkinsville",
    "Richmond",
    "Florence",
    "Georgetown",
    "Henderson",
  ],
  Louisiana: [
    "New Orleans",
    "Baton Rouge",
    "Shreveport",
    "Metairie",
    "Lafayette",
    "Lake Charles",
    "Kenner",
    "Bossier City",
    "Monroe",
    "Alexandria",
  ],
  Maine: [
    "Portland",
    "Lewiston",
    "Bangor",
    "South Portland",
    "Auburn",
    "Biddeford",
    "Sanford",
    "Augusta",
    "Saco",
    "Westbrook",
  ],
  Maryland: [
    "Baltimore",
    "Columbia",
    "Germantown",
    "Silver Spring",
    "Waldorf",
    "Glen Burnie",
    "Frederick",
    "Ellicott City",
    "Dundalk",
    "Rockville",
  ],
  Massachusetts: [
    "Boston",
    "Worcester",
    "Springfield",
    "Lowell",
    "Cambridge",
    "New Bedford",
    "Brockton",
    "Quincy",
    "Lynn",
    "Fall River",
  ],
  Michigan: [
    "Detroit",
    "Grand Rapids",
    "Warren",
    "Sterling Heights",
    "Ann Arbor",
    "Lansing",
    "Flint",
    "Dearborn",
    "Livonia",
    "Troy",
  ],
  Minnesota: [
    "Minneapolis",
    "Saint Paul",
    "Rochester",
    "Duluth",
    "Bloomington",
    "Brooklyn Park",
    "Plymouth",
    "Maple Grove",
    "Woodbury",
    "St. Cloud",
  ],
  Mississippi: [
    "Jackson",
    "Gulfport",
    "Southaven",
    "Hattiesburg",
    "Biloxi",
    "Meridian",
    "Tupelo",
    "Greenville",
    "Olive Branch",
    "Horn Lake",
  ],
  Missouri: [
    "Kansas City",
    "Saint Louis",
    "Springfield",
    "Columbia",
    "Independence",
    "Lee's Summit",
    "O'Fallon",
    "St. Joseph",
    "St. Charles",
    "Blue Springs",
  ],
  Montana: [
    "Billings",
    "Missoula",
    "Great Falls",
    "Bozeman",
    "Butte",
    "Helena",
    "Kalispell",
    "Havre",
    "Anaconda",
    "Miles City",
  ],
  Nebraska: [
    "Omaha",
    "Lincoln",
    "Bellevue",
    "Grand Island",
    "Kearney",
    "Fremont",
    "Hastings",
    "North Platte",
    "Norfolk",
    "Columbus",
  ],
  Nevada: [
    "Las Vegas",
    "Henderson",
    "Reno",
    "North Las Vegas",
    "Sparks",
    "Carson City",
    "Fernley",
    "Elko",
    "Mesquite",
    "Boulder City",
  ],
  "New Hampshire": [
    "Manchester",
    "Nashua",
    "Concord",
    "Derry",
    "Dover",
    "Rochester",
    "Salem",
    "Merrimack",
    "Hudson",
    "Londonderry",
  ],
  "New Jersey": [
    "Newark",
    "Jersey City",
    "Paterson",
    "Elizabeth",
    "Edison",
    "Woodbridge",
    "Lakewood",
    "Toms River",
    "Hamilton",
    "Trenton",
  ],
  "New Mexico": [
    "Albuquerque",
    "Las Cruces",
    "Rio Rancho",
    "Santa Fe",
    "Roswell",
    "Farmington",
    "Clovis",
    "Hobbs",
    "Alamogordo",
    "Carlsbad",
  ],
  "New York": [
    "New York City",
    "Buffalo",
    "Rochester",
    "Yonkers",
    "Syracuse",
    "Albany",
    "New Rochelle",
    "Mount Vernon",
    "Schenectady",
    "Utica",
  ],
  "North Carolina": [
    "Charlotte",
    "Raleigh",
    "Greensboro",
    "Durham",
    "Winston-Salem",
    "Fayetteville",
    "Cary",
    "Wilmington",
    "High Point",
    "Concord",
  ],
  "North Dakota": [
    "Fargo",
    "Bismarck",
    "Grand Forks",
    "Minot",
    "West Fargo",
    "Williston",
    "Dickinson",
    "Mandan",
    "Jamestown",
    "Wahpeton",
  ],
  Ohio: [
    "Columbus",
    "Cleveland",
    "Cincinnati",
    "Toledo",
    "Akron",
    "Dayton",
    "Parma",
    "Canton",
    "Youngstown",
    "Lorain",
  ],
  Oklahoma: [
    "Oklahoma City",
    "Tulsa",
    "Norman",
    "Broken Arrow",
    "Lawton",
    "Edmond",
    "Moore",
    "Midwest City",
    "Enid",
    "Stillwater",
  ],
  Oregon: [
    "Portland",
    "Salem",
    "Eugene",
    "Gresham",
    "Hillsboro",
    "Beaverton",
    "Bend",
    "Medford",
    "Springfield",
    "Corvallis",
  ],
  Pennsylvania: [
    "Philadelphia",
    "Pittsburgh",
    "Allentown",
    "Erie",
    "Reading",
    "Scranton",
    "Bethlehem",
    "Lancaster",
    "Harrisburg",
    "Altoona",
  ],
  "Rhode Island": [
    "Providence",
    "Cranston",
    "Warwick",
    "Pawtucket",
    "East Providence",
    "Woonsocket",
    "Coventry",
    "Cumberland",
    "North Providence",
    "West Warwick",
  ],
  "South Carolina": [
    "Columbia",
    "Charleston",
    "North Charleston",
    "Mount Pleasant",
    "Rock Hill",
    "Greenville",
    "Summerville",
    "Sumter",
    "Goose Creek",
    "Hilton Head",
  ],
  "South Dakota": [
    "Sioux Falls",
    "Rapid City",
    "Aberdeen",
    "Brookings",
    "Watertown",
    "Mitchell",
    "Yankton",
    "Pierre",
    "Huron",
    "Vermillion",
  ],
  Tennessee: [
    "Memphis",
    "Nashville",
    "Knoxville",
    "Chattanooga",
    "Clarksville",
    "Murfreesboro",
    "Franklin",
    "Jackson",
    "Johnson City",
    "Bartlett",
  ],
  Texas: [
    "Houston",
    "San Antonio",
    "Dallas",
    "Austin",
    "Fort Worth",
    "El Paso",
    "Arlington",
    "Corpus Christi",
    "Plano",
    "Laredo",
  ],
  Utah: [
    "Salt Lake City",
    "West Valley City",
    "Provo",
    "West Jordan",
    "Orem",
    "Sandy",
    "Ogden",
    "St. George",
    "Layton",
    "South Jordan",
  ],
  Vermont: [
    "Burlington",
    "South Burlington",
    "Rutland",
    "Barre",
    "Montpelier",
    "Winooski",
    "St. Albans",
    "Newport",
    "Vergennes",
    "Middlebury",
  ],
  Virginia: [
    "Virginia Beach",
    "Norfolk",
    "Chesapeake",
    "Richmond",
    "Newport News",
    "Alexandria",
    "Hampton",
    "Roanoke",
    "Portsmouth",
    "Suffolk",
  ],
  Washington: [
    "Seattle",
    "Spokane",
    "Tacoma",
    "Vancouver",
    "Bellevue",
    "Kent",
    "Everett",
    "Renton",
    "Spokane Valley",
    "Kirkland",
  ],
  "West Virginia": [
    "Charleston",
    "Huntington",
    "Morgantown",
    "Parkersburg",
    "Wheeling",
    "Weirton",
    "Fairmont",
    "Martinsburg",
    "Beckley",
    "Clarksburg",
  ],
  Wisconsin: [
    "Milwaukee",
    "Madison",
    "Green Bay",
    "Kenosha",
    "Racine",
    "Appleton",
    "Waukesha",
    "Eau Claire",
    "Oshkosh",
    "Janesville",
  ],
  Wyoming: [
    "Cheyenne",
    "Casper",
    "Laramie",
    "Gillette",
    "Rock Springs",
    "Sheridan",
    "Green River",
    "Evanston",
    "Riverton",
    "Jackson",
  ],
};

/* ══════════════════════════════════════════════════════════════════
   RESPONSIVE HELPERS
══════════════════════════════════════════════════════════════════ */

function useIsMobile(breakpoint = 900) {
  const getValue = () =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false;

  const [isMobile, setIsMobile] = useState(getValue);

  useEffect(() => {
    const onResize = () => setIsMobile(getValue());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function useIsTablet(breakpoint = 1200) {
  const getValue = () =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false;

  const [isTablet, setIsTablet] = useState(getValue);

  useEffect(() => {
    const onResize = () => setIsTablet(getValue());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isTablet;
}

/* ══════════════════════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════════════════════ */

function formatUSPhone(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
function rawDigits(v) {
  return (v || "").replace(/\D/g, "");
}
function isValidUSPhone(v) {
  return rawDigits(v).length === 10;
}
function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}
function isValidDomain(v) {
  const host = (v || "").replace(/^https?:\/\//i, "").split("/")[0];
  return /^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/.test(
    host,
  );
}
function isValidHttpsUrl(v) {
  const url = v?.startsWith("http") ? v : `https://${v}`;
  try {
    return new URL(url).protocol === "https:";
  } catch {
    return false;
  }
}
function normalizeUrl(v) {
  if (!v) return v;
  return v.startsWith("https://") || v.startsWith("http://")
    ? v
    : `https://${v}`;
}

/* ══════════════════════════════════════════════════════════════════
   SHARED UI ATOMS
══════════════════════════════════════════════════════════════════ */

const labelStyle = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.67rem",
  fontWeight: 500,
  letterSpacing: "0.04em",
  color: "#6b7280",
  marginBottom: 6,
};

function Divider() {
  return <div style={{ height: 1, background: "#edf0f4", margin: "4px 0" }} />;
}

function StepHead({ eyebrow, title, titleAccent, sub }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.62rem",
          letterSpacing: "0.04em",
          color: "#9ca3af",
          marginBottom: 8,
        }}
      >
        {eyebrow}
      </div>
      <h2
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "clamp(1.8rem, 2.2vw, 2.2rem)",
          lineHeight: 1.1,
          letterSpacing: "0.01em",
          color: "#111827",
          margin: "0 0 10px",
        }}
      >
        {title}
        {titleAccent && (
          <>
            {" "}
            <span style={{ color: "#111827" }}>{titleAccent}</span>
          </>
        )}
      </h2>
      {sub && (
        <p
          style={{
            fontFamily: "'DM Sans'",
            fontSize: "0.875rem",
            color: "#6b7280",
            margin: 0,
            lineHeight: 1.65,
          }}
        >
          {sub}
        </p>
      )}
    </div>
  );
}

function TypeCard({ icon: Icon, label, sub, selected, onClick, isMobile }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        width: isMobile ? "100%" : "auto",
        padding: isMobile ? "18px 16px" : "22px 18px",
        borderRadius: 12,
        cursor: "pointer",
        textAlign: "left",
        border: `2px solid ${selected ? "#111111" : "#e5e7eb"}`,
        background: selected ? "rgba(0,0,0,0.03)" : "#fafafa",
        transition: "all 0.15s",
        outline: "none",
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          marginBottom: 14,
          border: `1px solid ${selected ? "rgba(0,0,0,0.15)" : "#e5e7eb"}`,
          background: selected ? "rgba(0,0,0,0.06)" : "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: selected ? "#111111" : "#9ca3af",
          transition: "all 0.15s",
        }}
      >
        <Icon size={20} />
      </div>
      <div
        style={{
          fontFamily: "'DM Sans'",
          fontWeight: 700,
          fontSize: "1rem",
          color: selected ? "#111827" : "#374151",
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "'DM Sans'",
          fontSize: "0.8rem",
          color: "#9ca3af",
          lineHeight: 1.5,
        }}
      >
        {sub}
      </div>
    </button>
  );
}

function USPhoneField({ value, onChange, label = "Phone" }) {
  const isValid = !value || isValidUSPhone(value);
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={labelStyle}>
        {label} <span style={{ color: "#111111" }}>*</span>
      </div>
      <input
        type="tel"
        value={value}
        placeholder="(555) 000-0000"
        maxLength={14}
        onChange={(e) => onChange(formatUSPhone(e.target.value))}
        onFocus={(e) => {
          e.target.style.borderColor = "#111111";
          e.target.style.boxShadow = "0 0 0 3px rgba(0,0,0,0.10)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor =
            !isValid && value ? "#ef4444" : "#dde1e9";
          e.target.style.boxShadow = "none";
        }}
        style={{
          display: "block",
          width: "100%",
          boxSizing: "border-box",
          background: "#f8f9fb",
          border: `1px solid ${!isValid && value ? "#ef4444" : "#dde1e9"}`,
          borderRadius: 10,
          padding: "11px 13px",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.875rem",
          color: "#111827",
          outline: "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
      />
      {!isValid && value && (
        <div
          style={{
            fontFamily: "'DM Sans'",
            fontSize: "0.72rem",
            color: "#dc2626",
            marginTop: 4,
          }}
        >
          Enter a valid 10-digit US phone number
        </div>
      )}
    </div>
  );
}

function StepperBar({ steps, cur, isMobile }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: isMobile ? 4 : 6,
        overflowX: "auto",
        paddingBottom: isMobile ? 2 : 0,
      }}
    >
      {steps.map((label, i) => {
        const done = i < cur;
        const active = i === cur;
        const future = i > cur;
        return (
          <div
            key={label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 4 : 6,
            }}
          >
            <div
              style={{
                width: isMobile ? 20 : 22,
                height: isMobile ? 20 : 22,
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.6rem",
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                background: done
                  ? "#111111"
                  : active
                    ? "#111827"
                    : "transparent",
                border: `2px solid ${done ? "#111111" : active ? "#111827" : "#d1d5db"}`,
                color: done ? "#fff" : active ? "#fff" : "#d1d5db",
                transition: "all 0.2s",
              }}
            >
              {done ? (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path
                    d="M2 5l2.5 2.5L8 3"
                    stroke="#fff"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                i + 1
              )}
            </div>

            {active && !isMobile && (
              <span
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.6rem",
                  letterSpacing: "0.04em",
                  color: "#374151",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </span>
            )}

            {i < steps.length - 1 && (
              <div
                style={{
                  width:
                    future || active
                      ? isMobile
                        ? 10
                        : 16
                      : isMobile
                        ? 14
                        : 20,
                  height: 2,
                  borderRadius: 1,
                  background: done ? "#111111" : "#e5e7eb",
                  transition: "background 0.2s",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function StateField({ value, onChange, disabled }) {
  const isDisabled = disabled || false;
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={labelStyle}>
        State <span style={{ color: "#111111" }}>*</span>
      </div>
      <select
        value={value}
        onChange={(e) => !isDisabled && onChange(e.target.value)}
        disabled={isDisabled}
        onFocus={(e) => {
          if (!isDisabled) {
            e.target.style.borderColor = "#111111";
            e.target.style.boxShadow = "0 0 0 3px rgba(0,0,0,0.10)";
          }
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#dde1e9";
          e.target.style.boxShadow = "none";
        }}
        style={{
          display: "block",
          width: "100%",
          boxSizing: "border-box",
          background: isDisabled ? "#f3f4f6" : "#f8f9fb",
          border: "1px solid #dde1e9",
          borderRadius: 10,
          padding: "11px 13px",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.875rem",
          color: value ? "#111827" : "#9ca3af",
          outline: "none",
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.6 : 1,
          transition: "border-color 0.15s, box-shadow 0.15s",
          WebkitAppearance: "none",
          appearance: "none",
        }}
      >
        <option value="">Select state…</option>
        {US_STATES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
    </div>
  );
}

function CityField({ value, onChange, state, disabled }) {
  const cities = state ? CITIES_BY_STATE[state] || [] : [];
  const isDisabled = disabled || !state;
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={labelStyle}>
        City <span style={{ color: "#111111" }}>*</span>
      </div>
      <select
        value={value}
        onChange={(e) => !isDisabled && onChange(e.target.value)}
        disabled={isDisabled}
        onFocus={(e) => {
          if (!isDisabled) {
            e.target.style.borderColor = "#111111";
            e.target.style.boxShadow = "0 0 0 3px rgba(0,0,0,0.10)";
          }
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#dde1e9";
          e.target.style.boxShadow = "none";
        }}
        style={{
          display: "block",
          width: "100%",
          boxSizing: "border-box",
          background: isDisabled ? "#f3f4f6" : "#f8f9fb",
          border: "1px solid #dde1e9",
          borderRadius: 10,
          padding: "11px 13px",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.875rem",
          color: value ? "#111827" : "#9ca3af",
          outline: "none",
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.6 : 1,
          transition: "border-color 0.15s, box-shadow 0.15s",
          WebkitAppearance: "none",
          appearance: "none",
        }}
      >
        <option value="">
          {state
            ? disabled
              ? value || "—"
              : "Select city…"
            : "Select state first"}
        </option>
        {cities.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    </div>
  );
}

function AddressRow({
  street,
  city,
  state,
  zip,
  onStreet,
  onCity,
  onState,
  onZip,
  streetPlaceholder = "123 Main St",
  disabled = false,
}) {
  const isMobile = useIsMobile(900);
  const isTablet = useIsTablet(1200);

  const gridTemplateColumns = useMemo(() => {
    if (isMobile) return "1fr";
    if (isTablet) return "1fr 1fr";
    return "3fr 2fr 1.8fr 1.2fr";
  }, [isMobile, isTablet]);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns,
        gap: 8,
        alignItems: "end",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={labelStyle}>
          <MapPin size={11} style={{ color: "#9ca3af" }} /> Street{" "}
          <span style={{ color: "#111111" }}>*</span>
        </div>
        <input
          value={street}
          onChange={(e) => !disabled && onStreet(e.target.value)}
          placeholder={streetPlaceholder}
          disabled={disabled}
          onFocus={(e) => {
            if (!disabled) {
              e.target.style.borderColor = "#111111";
              e.target.style.boxShadow = "0 0 0 3px rgba(0,0,0,0.10)";
            }
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#dde1e9";
            e.target.style.boxShadow = "none";
          }}
          style={{
            display: "block",
            width: "100%",
            boxSizing: "border-box",
            background: disabled ? "#f3f4f6" : "#f8f9fb",
            border: "1px solid #dde1e9",
            borderRadius: 10,
            padding: "11px 10px",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.8rem",
            color: "#111827",
            outline: "none",
            cursor: disabled ? "not-allowed" : "text",
            opacity: disabled ? 0.6 : 1,
            transition: "border-color 0.15s, box-shadow 0.15s",
          }}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={labelStyle}>
          State <span style={{ color: "#111111" }}>*</span>
        </div>
        <select
          value={state}
          onChange={(e) => {
            if (!disabled) {
              onState(e.target.value);
              onCity("");
            }
          }}
          disabled={disabled}
          onFocus={(e) => {
            if (!disabled) {
              e.target.style.borderColor = "#111111";
              e.target.style.boxShadow = "0 0 0 3px rgba(0,0,0,0.10)";
            }
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#dde1e9";
            e.target.style.boxShadow = "none";
          }}
          style={{
            display: "block",
            width: "100%",
            boxSizing: "border-box",
            background: disabled ? "#f3f4f6" : "#f8f9fb",
            border: "1px solid #dde1e9",
            borderRadius: 10,
            padding: "11px 8px",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.8rem",
            color: state ? "#111827" : "#9ca3af",
            outline: "none",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            transition: "border-color 0.15s",
            WebkitAppearance: "none",
            appearance: "none",
          }}
        >
          <option value="">State…</option>
          {US_STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={labelStyle}>
          City <span style={{ color: "#111111" }}>*</span>
        </div>
        <select
          value={city}
          onChange={(e) => !disabled && onCity(e.target.value)}
          disabled={disabled || !state}
          onFocus={(e) => {
            if (!disabled && state) {
              e.target.style.borderColor = "#111111";
              e.target.style.boxShadow = "0 0 0 3px rgba(0,0,0,0.10)";
            }
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#dde1e9";
            e.target.style.boxShadow = "none";
          }}
          style={{
            display: "block",
            width: "100%",
            boxSizing: "border-box",
            background: disabled || !state ? "#f3f4f6" : "#f8f9fb",
            border: "1px solid #dde1e9",
            borderRadius: 10,
            padding: "11px 8px",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.8rem",
            color: city ? "#111827" : "#9ca3af",
            outline: "none",
            cursor: disabled || !state ? "not-allowed" : "pointer",
            opacity: disabled || !state ? 0.6 : 1,
            transition: "border-color 0.15s",
            WebkitAppearance: "none",
            appearance: "none",
          }}
        >
          <option value="">
            {state ? (disabled && city ? city : "City…") : "—"}
          </option>
          {state &&
            (CITIES_BY_STATE[state] || []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
        </select>
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={labelStyle}>
          ZIP <span style={{ color: "#111111" }}>*</span>
        </div>
        <input
          value={zip}
          onChange={(e) => !disabled && onZip(e.target.value)}
          placeholder="00000"
          maxLength={10}
          disabled={disabled}
          onFocus={(e) => {
            if (!disabled) {
              e.target.style.borderColor = "#111111";
              e.target.style.boxShadow = "0 0 0 3px rgba(0,0,0,0.10)";
            }
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#dde1e9";
            e.target.style.boxShadow = "none";
          }}
          style={{
            display: "block",
            width: "100%",
            boxSizing: "border-box",
            background: disabled ? "#f3f4f6" : "#f8f9fb",
            border: "1px solid #dde1e9",
            borderRadius: 10,
            padding: "11px 8px",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.8rem",
            color: "#111827",
            outline: "none",
            cursor: disabled ? "not-allowed" : "text",
            opacity: disabled ? 0.6 : 1,
            transition: "border-color 0.15s, box-shadow 0.15s",
          }}
        />
      </div>
    </div>
  );
}

function WebsiteField({ value, onChange }) {
  const [touched, setTouched] = useState(false);
  const isValid =
    !value ||
    (value.length > 0 &&
      /^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+/.test(
        value.replace(/^https?:\/\//, "").split("/")[0],
      ));
  const showErr = touched && value && !isValid;
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={labelStyle}>
        Website <span style={{ color: "#111111" }}>*</span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          background: "#f8f9fb",
          border: `1px solid ${showErr ? "#ef4444" : "#dde1e9"}`,
          borderRadius: 10,
          overflow: "hidden",
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
        onFocusCapture={(e) => {
          e.currentTarget.style.borderColor = showErr ? "#ef4444" : "#111111";
          e.currentTarget.style.boxShadow = "0 0 0 3px rgba(0,0,0,0.10)";
        }}
        onBlurCapture={(e) => {
          setTouched(true);
          e.currentTarget.style.borderColor = showErr ? "#ef4444" : "#dde1e9";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <span
          style={{
            padding: "11px 0 11px 13px",
            flexShrink: 0,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.8rem",
            color: "#9ca3af",
            userSelect: "none",
            whiteSpace: "nowrap",
          }}
        >
          https://
        </span>
        <input
          value={value}
          onChange={(e) => {
            let v = e.target.value.replace(/^https?:\/\//i, "");
            onChange(v);
          }}
          placeholder="acme.com"
          style={{
            flex: 1,
            background: "transparent",
            border: "none",
            outline: "none",
            padding: "11px 13px 11px 4px",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.875rem",
            color: "#111827",
            minWidth: 0,
          }}
        />
      </div>
      {showErr && (
        <div
          style={{
            fontFamily: "'DM Sans'",
            fontSize: "0.72rem",
            color: "#dc2626",
            marginTop: 4,
          }}
        >
          Enter a valid domain (e.g. acme.com)
        </div>
      )}
    </div>
  );
}

function InlineEmailField({
  value,
  onChange,
  label = "Contact email",
  placeholder = "info@acme.com",
  disabled = false,
}) {
  const [touched, setTouched] = useState(false);
  const isValid = !value || isValidEmail(value);
  const showErr = touched && value && !isValid;
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={labelStyle}>
        <Mail size={11} style={{ color: "#9ca3af" }} /> {label}{" "}
        <span style={{ color: "#111111" }}>*</span>
      </div>
      <input
        type="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        onBlur={() => setTouched(true)}
        onFocus={(e) => {
          e.target.style.borderColor = "#111111";
          e.target.style.boxShadow = "0 0 0 3px rgba(0,0,0,0.10)";
        }}
        style={{
          display: "block",
          width: "100%",
          boxSizing: "border-box",
          background: disabled ? "#f3f4f6" : "#f8f9fb",
          border: `1px solid ${showErr ? "#ef4444" : "#dde1e9"}`,
          borderRadius: 10,
          padding: "11px 13px",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.875rem",
          color: "#111827",
          outline: "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
        onBlurCapture={(e) => {
          setTouched(true);
          e.target.style.borderColor = showErr ? "#ef4444" : "#dde1e9";
          e.target.style.boxShadow = "none";
        }}
      />
      {showErr && (
        <div
          style={{
            fontFamily: "'DM Sans'",
            fontSize: "0.72rem",
            color: "#dc2626",
            marginTop: 4,
          }}
        >
          Enter a valid email address
        </div>
      )}
    </div>
  );
}

function LogoZone({ publicBrand }) {
  const [imgErr, setImgErr] = useState(false);
  const src = publicBrand?.logoUrl || "/assets/project_logo.png";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        marginBottom: 28,
        paddingBottom: 24,
        borderBottom: "1px solid #edf0f4",
      }}
    >
      {imgErr ? (
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 14,
            background: "#f0f2f5",
            border: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "1rem",
              color: "#111111",
              letterSpacing: "0.04em",
            }}
          >
            MDP
          </span>
        </div>
      ) : (
        <img
          src={src}
          alt="Logo"
          style={{ height: 64, maxWidth: 180, objectFit: "contain" }}
          onError={() => setImgErr(true)}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   STEP COMPONENTS
══════════════════════════════════════════════════════════════════ */

function Step0AccountType({ mode, setMode, onContinue }) {
  const isMobile = useIsMobile(900);

  return (
    <>
      <StepHead
        eyebrow="Create account"
        title="Choose"
        titleAccent="Account Type"
        sub="Select how you'll use the platform. You can always upgrade later."
      />
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 14,
          marginBottom: 32,
        }}
      >
        <TypeCard
          icon={UserRound}
          label="Individual"
          sub="Solo designer, homeowner, or independent contractor."
          selected={mode === "individual"}
          onClick={() => setMode("individual")}
          isMobile={isMobile}
        />
        <TypeCard
          icon={Building2}
          label="Company"
          sub="Team workspace with white-label branding and client tools."
          selected={mode === "organization"}
          onClick={() => setMode("organization")}
          isMobile={isMobile}
        />
      </div>
      <BtnPrimary onClick={onContinue}>
        Continue as {mode === "individual" ? "Individual" : "Company"}{" "}
        <ArrowRight size={15} />
      </BtnPrimary>
      <p
        style={{
          textAlign: "center",
          fontFamily: "'DM Sans'",
          fontSize: "0.84rem",
          color: "#9ca3af",
          marginTop: 20,
          marginBottom: 0,
        }}
      >
        Already have an account? <AuthLink to="/login">Sign in</AuthLink>
      </p>
    </>
  );
}

function StepCompanyProfile({ company, setCompany, onContinue, setError }) {
  const isMobile = useIsMobile(900);

  function handleContinue() {
    if (!company.name.trim()) return setError("Company name is required");
    if (!company.street.trim()) return setError("Company street is required");
    if (!company.state) return setError("Company state is required");
    if (!company.city) return setError("Company city is required");
    if (!company.zip.trim()) return setError("Company ZIP is required");
    if (!company.website.trim()) return setError("Company website is required");
    if (!isValidDomain(company.website))
      return setError("Website must be a valid domain (e.g. acme.com)");
    if (!company.contactEmail.trim())
      return setError("Contact email is required");
    if (!isValidEmail(company.contactEmail))
      return setError("Please enter a valid contact email");
    if (!isValidUSPhone(company.contactPhone))
      return setError("Please enter a valid US phone number for contact");
    setError(null);
    onContinue();
  }

  return (
    <>
      <StepHead
        eyebrow="Step 2 of 4 — Company"
        title="Company"
        titleAccent="Profile"
        sub="Your company details for your branded workspace."
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <FField
          label="Company name"
          value={company.name}
          onChange={(v) => setCompany((c) => ({ ...c, name: v }))}
          placeholder="Acme Steel Buildings"
          required
        />
        <Divider />
        <AddressRow
          street={company.street}
          city={company.city}
          state={company.state}
          zip={company.zip}
          streetPlaceholder="123 Industrial Blvd"
          onStreet={(v) => setCompany((c) => ({ ...c, street: v }))}
          onCity={(v) => setCompany((c) => ({ ...c, city: v }))}
          onState={(v) => setCompany((c) => ({ ...c, state: v, city: "" }))}
          onZip={(v) => setCompany((c) => ({ ...c, zip: v }))}
        />
        <Divider />
        <WebsiteField
          value={company.website}
          onChange={(v) => setCompany((c) => ({ ...c, website: v }))}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 10,
          }}
        >
          <InlineEmailField
            value={company.contactEmail}
            onChange={(v) => setCompany((c) => ({ ...c, contactEmail: v }))}
          />
          <USPhoneField
            label="Contact phone"
            value={company.contactPhone}
            onChange={(v) => setCompany((c) => ({ ...c, contactPhone: v }))}
          />
        </div>
        <BtnPrimary onClick={handleContinue} style={{ marginTop: 6 }}>
          Continue <ArrowRight size={15} />
        </BtnPrimary>
      </div>
    </>
  );
}

function StepPersonalProfile({
  eyebrow,
  profile,
  setProfile,
  onContinue,
  setError,
  showSameAsCompany,
  company,
}) {
  const isMobile = useIsMobile(900);
  const [sameAsCompany, setSameAsCompany] = useState(false);

  function toggleSame(checked) {
    setSameAsCompany(checked);
    if (checked && company) {
      setProfile((p) => ({
        ...p,
        street: company.street,
        city: company.city,
        state: company.state,
        zip: company.zip,
      }));
    }
  }

  function handleContinue() {
    if (!profile.firstName.trim()) return setError("First name is required");
    if (!profile.lastName.trim()) return setError("Last name is required");
    if (!profile.street.trim()) return setError("Street address is required");
    if (!profile.state) return setError("State is required");
    if (!profile.city) return setError("City is required");
    if (!profile.zip.trim()) return setError("ZIP code is required");
    if (!profile.birthday) return setError("Birthday is required");
    if (!profile.professional) return setError("Professional role is required");
    if (!isValidUSPhone(profile.phoneNumber))
      return setError("Please enter a valid US phone number");
    setError(null);
    onContinue();
  }

  return (
    <>
      <StepHead
        eyebrow={eyebrow}
        title="Your"
        titleAccent="Profile"
        sub="Tell us about yourself."
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 10,
          }}
        >
          <FField
            label="First name"
            value={profile.firstName}
            onChange={(v) => setProfile((p) => ({ ...p, firstName: v }))}
            placeholder="Jane"
            required
          />
          <FField
            label="Last name"
            value={profile.lastName}
            onChange={(v) => setProfile((p) => ({ ...p, lastName: v }))}
            placeholder="Doe"
            required
          />
        </div>
        <Divider />

        {showSameAsCompany && (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
            }}
            onClick={() => toggleSame(!sameAsCompany)}
          >
            <div
              style={{
                width: 17,
                height: 17,
                minWidth: 17,
                borderRadius: 4,
                border: `1.5px solid ${sameAsCompany ? "#111111" : "#d1d5db"}`,
                background: sameAsCompany ? "#111111" : "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.1s",
                flexShrink: 0,
              }}
            >
              <Check size={10} color={sameAsCompany ? "#fff" : "transparent"} />
            </div>
            <span
              style={{
                fontFamily: "'DM Sans'",
                fontSize: "0.82rem",
                color: "#374151",
              }}
            >
              Same address as company address
            </span>
          </label>
        )}

        <AddressRow
          street={profile.street}
          city={profile.city}
          state={profile.state}
          zip={profile.zip}
          streetPlaceholder="123 Main St"
          disabled={sameAsCompany}
          onStreet={(v) => setProfile((p) => ({ ...p, street: v }))}
          onCity={(v) => setProfile((p) => ({ ...p, city: v }))}
          onState={(v) => setProfile((p) => ({ ...p, state: v, city: "" }))}
          onZip={(v) => setProfile((p) => ({ ...p, zip: v }))}
        />

        <Divider />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 10,
          }}
        >
          <FField
            label="Birthday"
            icon={Calendar}
            type="date"
            value={profile.birthday}
            onChange={(v) => setProfile((p) => ({ ...p, birthday: v }))}
            required
          />
          <SelField
            label="Professional role"
            value={profile.professional}
            onChange={(v) => setProfile((p) => ({ ...p, professional: v }))}
            options={ROLES}
            required
          />
        </div>
        <USPhoneField
          value={profile.phoneNumber}
          onChange={(v) => setProfile((p) => ({ ...p, phoneNumber: v }))}
        />
        <BtnPrimary onClick={handleContinue} style={{ marginTop: 6 }}>
          Continue <ArrowRight size={15} />
        </BtnPrimary>
      </div>
    </>
  );
}

function StepCredentials({
  eyebrow,
  creds,
  setCreds,
  inviteToken,
  onSubmit,
  isSubmitting,
  setError,
}) {
  const isMobile = useIsMobile(900);
  const { score: pwScore, label: pwLabel } = pwStrength(creds.password);
  const pwMatch =
    creds.password &&
    creds.confirmPassword &&
    creds.password === creds.confirmPassword;
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [typedYes, setTypedYes] = useState("");
  const isConsentOk = agreeChecked && typedYes.trim().toUpperCase() === "YES";

  function handleSubmit() {
    if (!isValidEmail(creds.email))
      return setError("Please enter a valid email address");
    if (pwScore < 3) return setError("Please use a stronger password");
    if (!pwMatch) return setError("Passwords do not match");
    if (!isConsentOk)
      return setError('Please review the Terms and type "YES" to proceed.');
    setError(null);
    onSubmit();
  }

  return (
    <>
      <StepHead
        eyebrow={eyebrow}
        title="Your"
        titleAccent="Credentials"
        sub="Set up your login email and a strong password."
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <InlineEmailField
          label="Email address"
          placeholder="you@example.com"
          value={creds.email}
          onChange={(v) =>
            !inviteToken && setCreds((c) => ({ ...c, email: v }))
          }
          disabled={!!inviteToken}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 10,
          }}
        >
          <FField
            label="Password"
            icon={Lock}
            type="password"
            value={creds.password}
            onChange={(v) => setCreds((c) => ({ ...c, password: v }))}
            placeholder="Create password"
            required
          />
          <FField
            label="Confirm"
            icon={Lock}
            type="password"
            value={creds.confirmPassword}
            onChange={(v) => setCreds((c) => ({ ...c, confirmPassword: v }))}
            status={creds.confirmPassword ? (pwMatch ? "ok" : "er") : ""}
            required
          />
        </div>

        {creds.password && (
          <div>
            <StrBar score={pwScore} />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 5,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.66rem",
                color: "#9ca3af",
              }}
            >
              <span>Strength</span>
              <span
                style={{
                  color:
                    pwScore >= 4
                      ? "#16a34a"
                      : pwScore >= 3
                        ? "#555555"
                        : "#dc2626",
                }}
              >
                {pwLabel}
              </span>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => {
            const p = generatePassword();
            setCreds((c) => ({ ...c, password: p, confirmPassword: p }));
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            fontFamily: "'DM Sans'",
            fontSize: "0.8rem",
            color: "#111111",
            alignSelf: "flex-start",
          }}
        >
          <Sparkles size={13} /> Generate strong password
        </button>

        <Divider />

        <div
          style={{
            background: "#f8f9fb",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: "14px 16px",
          }}
        >
          <div
            style={{
              maxHeight: 80,
              overflowY: "auto",
              whiteSpace: "pre-line",
              fontFamily: "'DM Sans'",
              fontSize: "0.74rem",
              color: "#6b7280",
              lineHeight: 1.65,
              marginBottom: 12,
              scrollbarWidth: "thin",
            }}
          >
            {`Terms & Disclaimer (Summary)\nBy creating an account you agree to the Terms of Service and acknowledge the Disclaimer.\n`}
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              cursor: "pointer",
              marginBottom: 10,
            }}
            onClick={() => setAgreeChecked((a) => !a)}
          >
            <div
              style={{
                width: 17,
                height: 17,
                minWidth: 17,
                borderRadius: 4,
                marginTop: 1,
                border: `1.5px solid ${agreeChecked ? "#111111" : "#d1d5db"}`,
                background: agreeChecked ? "#111111" : "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.1s",
                flexShrink: 0,
              }}
            >
              <Check size={10} color={agreeChecked ? "#fff" : "transparent"} />
            </div>
            <span
              style={{
                fontFamily: "'DM Sans'",
                fontSize: "0.8rem",
                color: "#374151",
                lineHeight: 1.55,
              }}
            >
              I agree to the{" "}
              <Link
                to={`${process.env.REACT_APP_LANDING_URL}/terms`}
                target="_blank"
                rel="noreferrer"
                style={{
                  color: "#111111",
                  textDecoration: "none",
                  fontWeight: 500,
                }}
              >
                Terms of Service
              </Link>{" "}
              &amp;{" "}
              <Link
                to={`${process.env.REACT_APP_LANDING_URL}/privacy`}
                target="_blank"
                rel="noreferrer"
                style={{
                  color: "#111111",
                  textDecoration: "none",
                  fontWeight: 500,
                }}
              >
                Privacy Policy
              </Link>
            </span>
          </label>

          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
              gap: 12,
            }}
          >
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.6rem",
                color: "#9ca3af",
                letterSpacing: "0.04em",
                whiteSpace: isMobile ? "normal" : "nowrap",
              }}
            >
              Type "YES" to confirm:
            </span>
            <input
              value={typedYes}
              onChange={(e) => setTypedYes(e.target.value)}
              placeholder="YES"
              style={{
                width: isMobile ? "100%" : 86,
                padding: "7px 10px",
                background: "#fff",
                border: `1px solid ${typedYes.trim().toUpperCase() === "YES" ? "#22c55e" : "#d1d5db"}`,
                borderRadius: 8,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.88rem",
                letterSpacing: "0.12em",
                color:
                  typedYes.trim().toUpperCase() === "YES"
                    ? "#16a34a"
                    : "#111827",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
        </div>

        <BtnPrimary
          onClick={handleSubmit}
          disabled={isSubmitting || pwScore < 3 || !pwMatch || !isConsentOk}
          style={{ marginTop: 4 }}
        >
          {isSubmitting ? (
            <>
              <Spinner /> Creating account…
            </>
          ) : (
            <>
              {inviteToken ? "Accept Invitation" : "Create Account"}{" "}
              <ArrowRight size={15} />
            </>
          )}
        </BtnPrimary>

        <p
          style={{
            textAlign: "center",
            fontFamily: "'DM Sans'",
            fontSize: "0.82rem",
            color: "#9ca3af",
            margin: "4px 0 0",
          }}
        >
          Already have an account? <AuthLink to="/login">Sign in</AuthLink>
        </p>
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════════ */

export default function RegisterPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const inviteToken = sp.get("invite") || null;
  const pendingDesignToken =
    sp.get("pd") || localStorage.getItem("mdp.guest.pd") || null;
  const publicBrand = usePublicBrand();

  const isMobile = useIsMobile(900);
  const isTablet = useIsTablet(1200);

  const [step, setStep] = useState(inviteToken || pendingDesignToken ? 1 : 0);
  const [mode, setMode] = useState("individual");
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    birthday: "",
    professional: "",
    phoneNumber: "",
  });

  const [company, setCompany] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    website: "",
    contactEmail: "",
    contactPhone: "",
  });

  const [creds, setCreds] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });

  const IND_STEPS = ["Account Type", "Profile", "Credentials"];
  const ORG_STEPS = ["Account Type", "Company", "Profile", "Credentials"];
  const allSteps = mode === "organization" ? ORG_STEPS : IND_STEPS;
  const visSteps =
    inviteToken || pendingDesignToken ? allSteps.slice(1) : allSteps;
  const visCur = inviteToken || pendingDesignToken ? step - 1 : step;

  function back() {
    setError(null);
    if (step === 0) return nav("/login");
    if (step === 1 && (inviteToken || pendingDesignToken)) return nav("/login");
    setStep((s) => s - 1);
  }

  useEffect(() => {
    if (!inviteToken) return;
    (async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/invites/${encodeURIComponent(inviteToken)}`,
        );
        if (!r.ok) return setError("Invalid or expired invite.");
        const d = await r.json();
        setCreds((c) => ({ ...c, email: d.email }));
      } catch {
        setError("This invite link is invalid or has expired.");
      }
    })();
  }, [inviteToken]);

  const advanceTo = useCallback((n) => {
    setError(null);
    setStep(n);
  }, []);

  async function submit() {
    setIsSubmitting(true);
    setError(null);
    try {
      const addr =
        `${profile.street}, ${profile.city}, ${profile.state} ${profile.zip}`
          .replace(/\s+,/g, ",")
          .trim();

      const compAddr =
        mode === "organization"
          ? `${company.street}, ${company.city}, ${company.state} ${company.zip}`
              .replace(/\s+,/g, ",")
              .trim()
          : undefined;

      await auth.register({
        mode,
        firstname: profile.firstName,
        lastname: profile.lastName,
        username: "user" + Date.now(),
        address: addr,
        professional: profile.professional,
        country: "United States",
        birthday: profile.birthday,
        email: creds.email,
        password: creds.password,
        phonenumber: `+1${rawDigits(profile.phoneNumber)}`,
        inviteToken: inviteToken || undefined,
        pendingDesignToken: pendingDesignToken || undefined,
        companyName: mode === "organization" ? company.name : undefined,
        companyAddress: compAddr,
        companyWebsite:
          mode === "organization" ? normalizeUrl(company.website) : undefined,
        companyContactEmail:
          mode === "organization" ? company.contactEmail : undefined,
        companyContactPhone:
          mode === "organization"
            ? `+1${rawDigits(company.contactPhone)}`
            : undefined,
        companyCountry: mode === "organization" ? "United States" : undefined,
      });

      localStorage.removeItem("mdp.guest.pd");
      localStorage.removeItem("mdp.guest.design.v1");
      nav("/verify-otp?email=" + encodeURIComponent(creds.email));
    } catch (e) {
      setError(e?.data?.message || "Registration failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  function renderStep() {
    if (step === 0)
      return (
        <Step0AccountType
          mode={mode}
          setMode={setMode}
          onContinue={() => advanceTo(1)}
        />
      );

    if (mode === "individual") {
      if (step === 1)
        return (
          <StepPersonalProfile
            eyebrow="Step 2 of 3 — Profile"
            profile={profile}
            setProfile={setProfile}
            onContinue={() => advanceTo(2)}
            setError={setError}
          />
        );
      if (step === 2)
        return (
          <StepCredentials
            eyebrow="Step 3 of 3 — Credentials"
            creds={creds}
            setCreds={setCreds}
            inviteToken={inviteToken}
            onSubmit={submit}
            isSubmitting={isSubmitting}
            setError={setError}
          />
        );
    }

    if (mode === "organization") {
      if (step === 1)
        return (
          <StepCompanyProfile
            company={company}
            setCompany={setCompany}
            onContinue={() => advanceTo(2)}
            setError={setError}
          />
        );
      if (step === 2)
        return (
          <StepPersonalProfile
            eyebrow="Step 3 of 4 — Account Profile"
            profile={profile}
            setProfile={setProfile}
            onContinue={() => advanceTo(3)}
            setError={setError}
            showSameAsCompany
            company={company}
          />
        );
      if (step === 3)
        return (
          <StepCredentials
            eyebrow="Step 4 of 4 — Credentials"
            creds={creds}
            setCreds={setCreds}
            inviteToken={inviteToken}
            onSubmit={submit}
            isSubmitting={isSubmitting}
            setError={setError}
          />
        );
    }

    if (inviteToken || pendingDesignToken) {
      if (step === 1)
        return (
          <StepPersonalProfile
            eyebrow="Your Profile"
            profile={profile}
            setProfile={setProfile}
            onContinue={() => advanceTo(2)}
            setError={setError}
          />
        );
      if (step === 2)
        return (
          <StepCredentials
            eyebrow="Your Credentials"
            creds={creds}
            setCreds={setCreds}
            inviteToken={inviteToken}
            onSubmit={submit}
            isSubmitting={isSubmitting}
            setError={setError}
          />
        );
    }

    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        fontFamily: "'DM Sans', sans-serif",
        background: "#ffffff",
      }}
    >
      {!isMobile && <LeftPanel />}

      <div
        style={{
          width: isMobile ? "100%" : "50%",
          minHeight: "100vh",
          background: "#ffffff",
          borderLeft: !isMobile ? "1px solid #e8ecf0" : "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          padding: isMobile
            ? "20px 16px 24px"
            : isTablet
              ? "32px 28px 28px"
              : "40px 40px 36px",
          overflowY: "auto",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: isMobile ? "100%" : isTablet ? 720 : "60%",
          }}
        >
          <LogoZone publicBrand={publicBrand} />

          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
              justifyContent: "space-between",
              gap: isMobile ? 12 : 16,
              marginBottom: 20,
            }}
          >
            <BackBtn onClick={back} style={{ marginBottom: 0 }}>
              {step === 0 || inviteToken || pendingDesignToken
                ? "Back to Sign In"
                : "Back"}
            </BackBtn>

            {!inviteToken && !pendingDesignToken && (
              <StepperBar steps={visSteps} cur={visCur} isMobile={isMobile} />
            )}
          </div>

          <ErrBox msg={error} />
          {renderStep()}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            marginTop: "auto",
            paddingTop: 28,
            flexWrap: "wrap",
            textAlign: "center",
          }}
        >
          <Link
            to={`${process.env.REACT_APP_LANDING_URL}/terms`}
            style={{
              fontFamily: "'DM Sans'",
              fontSize: "0.75rem",
              color: "#9ca3af",
              textDecoration: "none",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#6b7280")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
          >
            Terms &amp; Conditions
          </Link>
          <span style={{ color: "#d1d5db", fontSize: "0.75rem" }}>·</span>
          <Link
            to={`${process.env.REACT_APP_LANDING_URL}/privacy`}
            style={{
              fontFamily: "'DM Sans'",
              fontSize: "0.75rem",
              color: "#9ca3af",
              textDecoration: "none",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#6b7280")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
